// eslint-disable-next-line ember/no-computed-properties-in-native-classes
import { and, gt, not, or, reads } from '@ember/object/computed';
import { buildValidations, validator } from 'ember-cp-validations';
// eslint-disable-next-line ember/no-computed-properties-in-native-classes
import { computed } from '@ember/object';
import { dependentKeyCompat } from '@ember/object/compat';
import { deserializeBirthDate, serializeBirthDate } from 'client-portal/utils/birth-date';
import { fragmentArray } from 'ember-data-model-fragments/attributes';
import { service } from '@ember/service';
import Model, { attr, belongsTo } from '@ember-data/model';
import moment from 'moment-timezone';

const disableEmergencyField = requirementKey => {
  return computed(`model.{${requirementKey},isCouple,isMinor}`, function () {
    if (!this.get(`model.${requirementKey}`)) return true;

    const isShown = this.get('model.isCouple') || this.get('model.isMinor');
    return !isShown;
  });
};

const Validations = buildValidations({
  // these should only be validated either way
  addressCity: [
    validator('presence', {
      presence: true,
      disabled: not('model.validateInsuranceInfo'),
      dependentKeys: [
        'model.validateInsuranceInfo',
        'currentPractice.claimAssistantSettingActive',
        'currentPractice.featureThirdPartyBilling',
      ],
      descriptionKey: 'errors.documentRequestContactInfoConnection.addressCity',
      messageKey: 'errors.presence',
    }),
    validator('presence', {
      presence: true,
      disabled: not('model.isRequiredAddressStreet'),
      descriptionKey: 'errors.documentRequestContactInfoConnection.addressCity',
      messageKey: 'errors.presence',
    }),
    validator('ds-error'),
  ],
  addressState: [
    validator('presence', {
      presence: true,
      disabled: not('model.validateInsuranceInfo'),
      dependentKeys: [
        'model.validateInsuranceInfo',
        'currentPractice.claimAssistantSettingActive',
        'currentPractice.featureThirdPartyBilling',
      ],
      descriptionKey: 'errors.documentRequestContactInfoConnection.addressState',
      messageKey: 'errors.presence',
    }),
    validator('presence', {
      presence: true,
      disabled: not('model.isRequiredAddressStreet'),
      descriptionKey: 'errors.documentRequestContactInfoConnection.addressState',
      messageKey: 'errors.presence',
    }),
    validator('ds-error'),
  ],
  addressStreet: [
    validator('presence', {
      presence: true,
      disabled: not('model.validateInsuranceInfo'),
      dependentKeys: [
        'model.validateInsuranceInfo',
        'currentPractice.claimAssistantSettingActive',
        'currentPractice.featureThirdPartyBilling',
      ],
      descriptionKey: 'errors.documentRequestContactInfoConnection.addressStreet',
      messageKey: 'errors.presence',
    }),
    validator('presence', {
      presence: true,
      disabled: not('model.isRequiredAddressStreet'),
      descriptionKey: 'errors.documentRequestContactInfoConnection.addressStreet',
      messageKey: 'errors.presence',
    }),
    validator('ds-error'),
  ],
  addressZip: [
    validator('presence', {
      presence: true,
      disabled: not('model.validateInsuranceInfo'),
      dependentKeys: [
        'model.validateInsuranceInfo',
        'currentPractice.claimAssistantSettingActive',
        'currentPractice.featureThirdPartyBilling',
      ],
      descriptionKey: 'errors.documentRequestContactInfoConnection.addressZip',
      messageKey: 'errors.presence',
    }),
    validator('presence', {
      presence: true,
      disabled: not('model.isRequiredAddressStreet'),
      descriptionKey: 'errors.documentRequestContactInfoConnection.addressZip',
      messageKey: 'errors.presence',
    }),
    validator('ds-error'),
  ],
  birthDate: [
    validator('presence', {
      presence: true,
      disabled: not('model.validateInsuranceInfo'),
      dependentKeys: [
        'model.validateInsuranceInfo',
        'currentPractice.claimAssistantSettingActive',
        'currentPractice.featureThirdPartyBilling',
      ],
      descriptionKey: 'errors.documentRequestContactInfoConnection.birthDate',
      messageKey: 'errors.presence',
    }),
    validator('presence', {
      presence: true,
      disabled: not('model.isRequiredBirthDate'),
      descriptionKey: 'errors.documentRequestContactInfoConnection.birthDate',
      messageKey: 'errors.presence',
    }),
    validator('ds-error'),
  ],
  eligibleInsuranceProviderName: [
    validator('presence', {
      presence: true,
      disabled: not('model.validateInsuranceInfo'),
      dependentKeys: [
        'model.validateInsuranceInfo',
        'currentPractice.claimAssistantSettingActive',
        'currentPractice.featureThirdPartyBilling',
      ],
      descriptionKey: 'errors.documentRequestContactInfoConnection.eligibleInsuranceProviderName',
      messageKey: 'errors.presence',
    }),
    validator('presence', {
      presence: true,
      disabled: not('model.askForInsuranceInfo'),
      descriptionKey: 'errors.documentRequestContactInfoConnection.eligibleInsuranceProviderName',
      messageKey: 'errors.presence',
    }),
    validator('ds-error'),
  ],
  emailAddress: [
    validator('format', {
      type: 'email',
      allowBlank: true,
      disabled: reads('model.isCouple'),
      messageKey: 'errors.documentRequestContactInfoConnection.emailAddress',
    }),
    validator('presence', {
      presence: true,
      disabled: or('model.isMinor', 'model.isCouple'),
      messageKey: 'errors.documentRequestContactInfoConnection.emailAddress',
    }),
    validator('ds-error'),
  ],
  emergencyContactEmailAddress: [
    validator('format', {
      type: 'email',
      allowBlank: true,
      descriptionKey: 'errors.documentRequestContactInfoConnection.emergencyContactEmailAddress',
      messageKey: 'errors.format.email',
    }),
    validator('presence', {
      presence: true,
      disabled: disableEmergencyField('isRequiredEmergencyContactEmailAddress'),
      descriptionKey: 'errors.documentRequestContactInfoConnection.emergencyContactEmailAddress',
      messageKey: 'errors.presence',
    }),
    validator('ds-error'),
  ],
  emergencyContactFirstName: [
    validator('presence', {
      presence: true,
      disabled: disableEmergencyField('isRequiredEmergencyContactNameInfo'),
      descriptionKey: 'errors.documentRequestContactInfoConnection.emergencyContactFirstName',
      messageKey: 'errors.presence',
    }),
    validator('ds-error'),
  ],
  emergencyContactLastName: [
    validator('presence', {
      presence: true,
      disabled: disableEmergencyField('isRequiredEmergencyContactNameInfo'),
      descriptionKey: 'errors.documentRequestContactInfoConnection.emergencyContactLastName',
      messageKey: 'errors.presence',
    }),
    validator('ds-error'),
  ],
  emergencyContactPhoneNumber: [
    validator('ds-error'),
    validator('presence', {
      presence: true,
      disabled: disableEmergencyField('isRequiredEmergencyContactPhoneNumber'),
      descriptionKey: 'errors.documentRequestContactInfoConnection.emergencyContactPhoneNumber',
      messageKey: 'errors.presence',
    }),
  ],
  emergencyContactType: [
    validator('presence', {
      presence: true,
      disabled: disableEmergencyField('isRequiredEmergencyContactType'),
      descriptionKey: 'errors.documentRequestContactInfoConnection.emergencyContactType',
      messageKey: 'errors.presence',
    }),
    validator('ds-error'),
  ],
  employmentStatus: [validator('ds-error')],
  firstName: [
    validator('presence', {
      presence: true,
      disabled: not('model.validateInsuranceInfo'),
      dependentKeys: [
        'model.validateInsuranceInfo',
        'currentPractice.claimAssistantSettingActive',
        'currentPractice.featureThirdPartyBilling',
      ],
      descriptionKey: 'errors.documentRequestContactInfoConnection.firstName',
      messageKey: 'errors.presence',
    }),
    validator('presence', {
      presence: true,
      disabled: false,
      descriptionKey: 'errors.documentRequestContactInfoConnection.firstName',
      messageKey: 'errors.presence',
    }),
    validator('ds-error'),
  ],
  genderInfo: [validator('ds-error')],
  // these should only be validated if the relationship is not self
  insuranceGroupId: [validator('ds-error')],
  insuranceInsuredAddress: [
    validator('presence', {
      presence: true,
      disabled: computed('model.{validateInsuranceInfo,isDependent}', function () {
        return !this.get('model.validateInsuranceInfo') || !this.get('model.isDependent');
      }),
      dependentKeys: [
        'currentPractice.claimAssistantSettingActive',
        'currentPractice.featureThirdPartyBilling',
        'model.askForInsurance',
        'model.isDependent',
      ],
      descriptionKey: 'errors.documentRequestContactInfoConnection.insuranceInsuredAddress',
      messageKey: 'errors.presence',
    }),
    validator('presence', {
      presence: true,
      disabled: not('model.isPrimaryInsuredInfoRequired'),
      descriptionKey: 'errors.documentRequestContactInfoConnection.insuranceInsuredAddress',
      messageKey: 'errors.presence',
    }),
    validator('ds-error'),
  ],
  insuranceInsuredCity: [
    validator('presence', {
      presence: true,
      disabled: computed('model.{validateInsuranceInfo,isDependent}', function () {
        return !this.get('model.validateInsuranceInfo') || !this.get('model.isDependent');
      }),
      dependentKeys: [
        'currentPractice.claimAssistantSettingActive',
        'currentPractice.featureThirdPartyBilling',
        'model.askForInsurance',
        'model.isDependent',
      ],
      descriptionKey: 'errors.documentRequestContactInfoConnection.insuranceInsuredCity',
      messageKey: 'errors.presence',
    }),
    validator('presence', {
      presence: true,
      disabled: not('model.isPrimaryInsuredInfoRequired'),
      descriptionKey: 'errors.documentRequestContactInfoConnection.insuranceInsuredCity',
      messageKey: 'errors.presence',
    }),
    validator('ds-error'),
  ],
  insuranceInsuredDob: [
    validator('presence', {
      presence: true,
      disabled: computed('model.{validateInsuranceInfo,isDependent}', function () {
        return !this.get('model.validateInsuranceInfo') || !this.get('model.isDependent');
      }),
      dependentKeys: [
        'currentPractice.claimAssistantSettingActive',
        'currentPractice.featureThirdPartyBilling',
        'model.askForInsurance',
        'model.isDependent',
      ],
      descriptionKey: 'errors.documentRequestContactInfoConnection.insuranceInsuredDob',
      messageKey: 'errors.presence',
    }),
    validator('presence', {
      presence: true,
      disabled: not('model.isPrimaryInsuredInfoRequired'),
      descriptionKey: 'errors.documentRequestContactInfoConnection.insuranceInsuredDob',
      messageKey: 'errors.presence',
    }),
    validator('ds-error'),
  ],
  insuranceInsuredFname: [
    validator('presence', {
      presence: true,
      disabled: computed('model.{validateInsuranceInfo,isDependent}', function () {
        return !this.get('model.validateInsuranceInfo') || !this.get('model.isDependent');
      }),
      dependentKeys: [
        'currentPractice.claimAssistantSettingActive',
        'currentPractice.featureThirdPartyBilling',
        'model.askForInsurance',
        'model.isDependent',
      ],
      descriptionKey: 'errors.documentRequestContactInfoConnection.insuranceInsuredFname',
      messageKey: 'errors.presence',
    }),
    validator('presence', {
      presence: true,
      disabled: not('model.isPrimaryInsuredInfoRequired'),
      descriptionKey: 'errors.documentRequestContactInfoConnection.insuranceInsuredFname',
      messageKey: 'errors.presence',
    }),
    validator('ds-error'),
  ],
  insuranceInsuredLname: [
    validator('presence', {
      presence: true,
      disabled: computed('model.{validateInsuranceInfo,isDependent}', function () {
        return !this.get('model.validateInsuranceInfo') || !this.get('model.isDependent');
      }),
      dependentKeys: [
        'currentPractice.claimAssistantSettingActive',
        'currentPractice.featureThirdPartyBilling',
        'model.askForInsurance',
        'model.isDependent',
      ],
      descriptionKey: 'errors.documentRequestContactInfoConnection.insuranceInsuredLname',
      messageKey: 'errors.presence',
    }),
    validator('presence', {
      presence: true,
      disabled: not('model.isPrimaryInsuredInfoRequired'),
      descriptionKey: 'errors.documentRequestContactInfoConnection.insuranceInsuredLname',
      messageKey: 'errors.presence',
    }),
    validator('ds-error'),
  ],
  insuranceInsuredPhone: [
    validator('presence', {
      presence: true,
      disabled: computed('model.{validateInsuranceInfo,isDependent}', function () {
        return !this.get('model.validateInsuranceInfo') || !this.get('model.isDependent');
      }),
      dependentKeys: [
        'currentPractice.claimAssistantSettingActive',
        'currentPractice.featureThirdPartyBilling',
        'model.askForInsurance',
        'model.isDependent',
      ],
      descriptionKey: 'errors.documentRequestContactInfoConnection.insuranceInsuredPhone',
      messageKey: 'errors.presence',
    }),
    validator('presence', {
      presence: true,
      disabled: not('model.isPrimaryInsuredInfoRequired'),
      descriptionKey: 'errors.documentRequestContactInfoConnection.insuranceInsuredPhone',
      messageKey: 'errors.presence',
    }),
    validator('ds-error'),
  ],
  insuranceInsuredRelation: [
    validator('presence', {
      presence: true,
      disabled: computed('model.{validateInsuranceInfo,isDependent}', function () {
        return !this.get('model.validateInsuranceInfo') || !this.get('model.isDependent');
      }),
      dependentKeys: [
        'currentPractice.claimAssistantSettingActive',
        'currentPractice.featureThirdPartyBilling',
        'model.askForInsurance',
        'model.isDependent',
      ],
      descriptionKey: 'errors.documentRequestContactInfoConnection.insuranceInsuredRelation',
      messageKey: 'errors.presence',
    }),
    validator('presence', {
      presence: true,
      disabled: not('model.isPrimaryInsuredInfoRequired'),
      descriptionKey: 'errors.documentRequestContactInfoConnection.insuranceInsuredRelation',
      messageKey: 'errors.presence',
    }),
    validator('ds-error'),
  ],
  insuranceInsuredSex: [
    validator('presence', {
      presence: true,
      disabled: computed('model.{validateInsuranceInfo,isDependent}', function () {
        return !this.get('model.validateInsuranceInfo') || !this.get('model.isDependent');
      }),
      dependentKeys: [
        'currentPractice.claimAssistantSettingActive',
        'currentPractice.featureThirdPartyBilling',
        'model.askForInsurance',
        'model.isDependent',
      ],
      descriptionKey: 'errors.documentRequestContactInfoConnection.insuranceInsuredSex',
      messageKey: 'errors.presence',
    }),
    validator('presence', {
      presence: true,
      disabled: not('model.isPrimaryInsuredInfoRequired'),
      descriptionKey: 'errors.documentRequestContactInfoConnection.insuranceInsuredSex',
      messageKey: 'errors.presence',
    }),
    validator('ds-error'),
  ],
  insuranceInsuredState: [
    validator('presence', {
      presence: true,
      disabled: computed('model.{validateInsuranceInfo,isDependent}', function () {
        return !this.get('model.validateInsuranceInfo') || !this.get('model.isDependent');
      }),
      dependentKeys: [
        'currentPractice.claimAssistantSettingActive',
        'currentPractice.featureThirdPartyBilling',
        'model.askForInsurance',
        'model.isDependent',
      ],
      descriptionKey: 'errors.documentRequestContactInfoConnection.insuranceInsuredState',
      messageKey: 'errors.presence',
    }),
    validator('presence', {
      presence: true,
      disabled: not('model.isPrimaryInsuredInfoRequired'),
      descriptionKey: 'errors.documentRequestContactInfoConnection.insuranceInsuredState',
      messageKey: 'errors.presence',
    }),
    validator('ds-error'),
  ],
  insuranceInsuredZip: [
    validator('presence', {
      presence: true,
      disabled: computed('model.{validateInsuranceInfo,isDependent}', function () {
        return !this.get('model.validateInsuranceInfo') || !this.get('model.isDependent');
      }),
      dependentKeys: [
        'currentPractice.claimAssistantSettingActive',
        'currentPractice.featureThirdPartyBilling',
        'model.askForInsurance',
        'model.isDependent',
      ],
      descriptionKey: 'errors.documentRequestContactInfoConnection.insuranceInsuredZip',
      messageKey: 'errors.presence',
    }),
    validator('presence', {
      presence: true,
      disabled: not('model.isPrimaryInsuredInfoRequired'),
      descriptionKey: 'errors.documentRequestContactInfoConnection.insuranceInsuredZip',
      messageKey: 'errors.presence',
    }),
    validator('ds-error'),
  ],
  insuranceMemberId: [
    validator('presence', {
      presence: true,
      disabled: not('model.validateInsuranceInfo'),
      dependentKeys: [
        'model.validateInsuranceInfo',
        'currentPractice.claimAssistantSettingActive',
        'currentPractice.featureThirdPartyBilling',
      ],
      descriptionKey: 'errors.documentRequestContactInfoConnection.insuranceMemberId',
      messageKey: 'errors.presence',
    }),
    validator('presence', {
      presence: true,
      disabled: not('model.askForInsuranceInfo'),
      descriptionKey: 'errors.documentRequestContactInfoConnection.insuranceMemberId',
      messageKey: 'errors.presence',
    }),
    validator('ds-error'),
  ],
  insurancePlanId: [validator('ds-error')],
  insuranceEffectiveStartDate: [validator('ds-error')],
  insuranceEffectiveEndDate: [validator('ds-error')],
  isMinor: [validator('ds-error')],
  lastName: [
    validator('presence', {
      presence: true,
      disabled: false,
      descriptionKey: 'errors.documentRequestContactInfoConnection.lastName',
      messageKey: 'errors.presence',
    }),
    validator('ds-error'),
  ],
  middleName: [validator('ds-error')],
  nickname: [validator('ds-error')],
  phoneNumber: [
    validator('presence', {
      presence: true,
      disabled: not('model.validateNonCoupleInsuranceInfo'),
      dependentKeys: [
        'model.validateInsuranceInfo',
        'currentPractice.claimAssistantSettingActive',
        'currentPractice.featureThirdPartyBilling',
      ],
      descriptionKey: 'errors.documentRequestContactInfoConnection.phoneNumber',
      messageKey: 'errors.presence',
    }),
    validator('presence', {
      presence: true,
      disabled: computed('model.{isRequiredPhoneNumber,isCouple,isMinor}', function () {
        if (this.get('model.isMinor') || this.get('model.isCouple')) return true;

        return !this.get('model.isRequiredPhoneNumber');
      }),
      descriptionKey: 'errors.documentRequestContactInfoConnection.phoneNumber',
      messageKey: 'errors.presence',
    }),
    validator('ds-error'),
  ],
  relationshipStatus: [validator('ds-error')],
  sex: [
    validator('presence', {
      presence: true,
      disabled: not('model.validateInsuranceInfo'),
      dependentKeys: [
        'model.validateInsuranceInfo',
        'currentPractice.claimAssistantSettingActive',
        'currentPractice.featureThirdPartyBilling',
      ],
      descriptionKey: 'errors.documentRequestContactInfoConnection.sex',
      messageKey: 'errors.presence',
    }),
    validator('presence', {
      presence: true,
      disabled: not('model.askForInsuranceInfo'),
      descriptionKey: 'errors.documentRequestContactInfoConnection.sex',
      messageKey: 'errors.presence',
    }),
    validator('ds-error'),
  ],
  timeZone: [validator('ds-error')],
  insuranceCardFrontId: [
    validator('presence', {
      presence: true,
      disabled: not('model.isRequiredInsuranceInfoCard'),
      descriptionKey: 'errors.documentRequestContactInfoConnection.insuranceCardFrontId',
      messageKey: 'errors.presence',
    }),
    validator('ds-error'),
  ],
  insuranceCardBackId: [
    validator('presence', {
      presence: true,
      disabled: not('model.isRequiredInsuranceInfoCard'),
      descriptionKey: 'errors.documentRequestContactInfoConnection.insuranceCardBackId',
      messageKey: 'errors.presence',
    }),
    validator('ds-error'),
  ],
  photoIdFrontId: [
    validator('presence', {
      presence: true,
      disabled: not('model.isRequiredPhotoIdUploaded'),
      descriptionKey: 'errors.documentRequestContactInfoConnection.photoIdFrontId',
      messageKey: 'errors.presence',
    }),
    validator('ds-error'),
  ],
  contactsListEmergencyContact: validator('inline', {
    validate(_value, _options, model) {
      if (model.hasActiveEmergencyContact) {
        return true;
      }

      return model.intl.t(
        'site.documents.contactInfo.contactsListSection.emergencyContactRequired'
      );
    },
    dependentKeys: [
      'model.contacts.@each.emergencyContact',
      'model.contacts.@each.isMarkedForDeletion',
      'model.contacts.@each.isDraft',
    ],
    disabled: not('model.shouldValidateContactsListEmergencyContact'),
  }),
});

const PRACTICE_OWNED_ATTRS = [
  'askForNickname',
  'askForGenderIdentity',
  'demographicFormRequiredFields',
  'askForInsurance',
  'practiceEnableEmailReminders',
  'practiceEnableSmsReminders',
  'practiceEnableVoiceReminders',
  'photoIdRequirement',
];

const CONTACT_AUTOSAVE_ATTRS = [
  'relationshipId',
  'firstName',
  'lastName',
  'relationshipName',
  'emergencyContact',
  'phoneNumber',
  'phoneType',
  'phoneLeaveVoiceMessage',
  'phoneLeaveTextMessage',
  'enableSmsvoiceReminders',
  'emailAddress',
  'emailType',
  'emailConsentGiven',
  'enableEmailReminders',
  'isRecipient',
];

export default class DemographicsFormModel extends Model.extend(Validations) {
  @service store;
  @service session;
  @service currentPractice;
  @service intl;
  @service clientInsuranceAccess;

  @belongsTo('document-request-contact-info', {
    async: false,
    inverse: 'demographicsForm',
  })
  documentRequestContactInfo;
  @belongsTo('insurance-provider-name', { async: false, inverse: null })
  eligibleInsuranceProviderName;

  @fragmentArray('contact-info/contact') contacts;

  @reads('documentRequestContactInfo.connection') connection;

  @reads('connection.insuranceCards') insuranceCards;
  @reads('connection.photoIds') photoIds;

  @reads('session.currentClient.isCouple') isCouple;
  @not('isCouple') isNotCouple;

  @gt('insuranceInsuredRelation', 0) isDependent;
  @and('eligibleInsuranceProviderName', 'insuranceMemberId') hasRequiredInsuranceInfo;

  get insuranceFeatureEnabled() {
    return this.clientInsuranceAccess.claimAssistantOrManagedBilling;
  }

  get isRequiredInsuranceCardUploaded() {
    return this.demographicFormRequiredFields.includes('insurance_card_uploaded');
  }

  get isPhotoIdSectionEnabled() {
    return this.photoIdRequirement !== 'disabled';
  }

  get isRequiredPhotoIdUploaded() {
    return this.photoIdRequirement === 'required';
  }

  @and('askForInsurance', 'isRequiredInsuranceInformation') askForInsuranceInfo;

  @and('askForInsurance', 'isRequiredInsuranceCardUploaded') isRequiredInsuranceInfoCard;

  @and('insuranceFeatureEnabled', 'askForInsuranceInfo') validateInsuranceInfo;
  @and('validateInsuranceInfo', 'isNotCouple') validateNonCoupleInsuranceInfo;

  @and('askForInsuranceInfo', 'isDependent') isPrimaryInsuredInfoRequired;

  @attr() firstName;
  @attr() lastName;
  @attr() middleName;
  @attr() nickname;
  @attr('boolean') isMinor;
  @attr('birth-date') birthDate;
  @attr() sex;
  @attr() genderInfo;
  @attr() relationshipStatus;
  @attr() employmentStatus;
  @attr() timeZone;

  @attr() emailAddress;
  @attr() emailType;
  @attr('boolean') emailEmailConsentGiven;
  @attr('boolean') enableEmailReminders;

  @attr() phoneNumber;
  @attr() phoneType;
  @attr('boolean') phoneLeaveVoiceMessage;
  @attr('boolean') phoneLeaveTextMessage;
  @attr() enableSmsvoiceReminders;

  @attr() addressStreet;
  @attr() addressCity;
  @attr() addressState;
  @attr() addressZip;

  @attr() emergencyContactFirstName;
  @attr() emergencyContactLastName;
  @attr() emergencyContactType;
  @attr() emergencyContactPhoneNumber;
  @attr() emergencyContactPhoneType;
  @attr('boolean') emergencyContactPhoneLeaveVoiceMessage;
  @attr('boolean') emergencyContactPhoneLeaveTextMessage;
  @attr() emergencyContactEnableSmsvoiceReminders;
  @attr() emergencyContactEmailAddress;
  @attr() emergencyContactEmailType;
  @attr('boolean') emergencyContactEnableEmailReminders;
  @attr('boolean') emergencyContactEmailEmailConsentGiven;

  @attr() insuranceMemberId;
  @attr() insuranceGroupId;
  @attr() insurancePlanId;
  @attr() insuranceInsuredRelation;
  @attr() insuranceInsuredFname;
  @attr() insuranceInsuredLname;
  @attr('moment') insuranceInsuredDob;
  @attr() insuranceInsuredSex;
  @attr() insuranceInsuredPhone;
  @attr() insuranceInsuredAddress;
  @attr() insuranceInsuredCity;
  @attr() insuranceInsuredState;
  @attr() insuranceInsuredZip;
  @attr('number') insuranceCardFrontId;
  @attr('number') insuranceCardBackId;
  @attr('number') photoIdFrontId;
  @attr('number') photoIdBackId;
  @attr('string') photoIdRequirement;
  @attr('moment') insuranceEffectiveStartDate;
  @attr('moment') insuranceEffectiveEndDate;
  @attr() insurancePaymentsTo;

  @attr('boolean') askForNickname;
  @attr('boolean') askForGenderIdentity;
  @attr('json', {
    defaultValue() {
      return [];
    },
  })
  demographicFormRequiredFields;
  @attr('boolean') askForInsurance;
  @attr('boolean') practiceEnableEmailReminders;
  @attr('boolean') practiceEnableSmsReminders;
  @attr('boolean') practiceEnableVoiceReminders;

  get isRequiredPhoneNumber() {
    return this.demographicFormRequiredFields.includes('phone_number');
  }

  get isRequiredAddressStreet() {
    return this.demographicFormRequiredFields.includes('address_street');
  }

  get isRequiredBirthDate() {
    return this.demographicFormRequiredFields.includes('birth_date');
  }

  get isRequiredEmergencyContactName() {
    return this.demographicFormRequiredFields.includes('emergency_contact_name');
  }

  get isRequiredEmergencyContactEmailAddress() {
    return this.demographicFormRequiredFields.includes('emergency_contact_email_address');
  }

  get isRequiredEmergencyContactPhoneNumber() {
    return this.demographicFormRequiredFields.includes('emergency_contact_phone_number');
  }

  get isRequiredInsuranceInformation() {
    return this.demographicFormRequiredFields.includes('insurance_information');
  }

  get isRequiredEmergencyContactNameInfo() {
    if (this.isCouple) return false;

    return this.isRequiredEmergencyContactName;
  }

  @or(
    'isRequiredEmergencyContactName',
    'isRequiredEmergencyContactEmailAddress',
    'isRequiredEmergencyContactPhoneNumber'
  )
  isRequiredEmergencyContactType;

  @dependentKeyCompat
  get shouldValidateContactsListEmergencyContact() {
    return !this.isMinor && !this.isCouple && this.isRequiredEmergencyContactType;
  }

  @dependentKeyCompat
  get activeContacts() {
    return this.contacts.filter(contact => {
      return !contact.isDraft && !contact.isMarkedForDeletion;
    });
  }

  @dependentKeyCompat
  get hasActiveEmergencyContact() {
    return this.activeContacts.some(contact => contact.emergencyContact);
  }

  get autosaveAttrs() {
    let attrs = [];
    this.constructor.fields.forEach((_, name) => attrs.push(name));
    attrs.removeObject('connection');
    attrs.removeObject('documentRequestContactInfo');
    attrs.removeObject('contacts');
    PRACTICE_OWNED_ATTRS.forEach(name => attrs.removeObject(name));
    return attrs;
  }

  autosaveSerialize() {
    let data = {};
    this.autosaveAttrs.forEach(name => {
      if (name === 'eligibleInsuranceProviderName' && this[name]) {
        return (data[name] = this[name].serialize({ includeId: true }));
      }
      if (name === 'birthDate') {
        data[name] = serializeBirthDate(this[name]);
        return;
      }

      data[name] = this[name] ?? null;
    });
    data.contacts = this._serializeContacts();
    return data;
  }

  _serializeContacts() {
    return this.contacts
      .filter(contact => !contact.isDraft)
      .map(contact => {
        let data = {};
        CONTACT_AUTOSAVE_ATTRS.forEach(name => {
          data[name] = contact[name] ?? null;
        });
        if (contact.isMarkedForDeletion) {
          data.isMarkedForDeletion = true;
        }
        return data;
      });
  }

  _restoreAttribute(name, data) {
    const DATE_FIELDS = [
      'birthDate',
      'insuranceInsuredDob',
      'insuranceEffectiveStartDate',
      'insuranceEffectiveEndDate',
    ];

    // eslint-disable-next-line no-prototype-builtins
    if (!data.hasOwnProperty(name)) {
      return;
    }

    const value = data[name];

    if (name === 'eligibleInsuranceProviderName' && value && typeof value === 'object') {
      this.store.pushPayload(value);
      this[name] = this.store.peekRecord('insurance-provider-name', value.data.id);
      return;
    }

    if (name === 'birthDate') {
      const parsed = deserializeBirthDate(value);
      this[name] = parsed;
      return;
    }

    if (DATE_FIELDS.includes(name)) {
      if (value === undefined || value === null) {
        this[name] = null;
        return;
      }
      const parsedDate = moment.parseZone(value);
      if (!parsedDate.isValid()) return;
      this[name] = parsedDate;
      return;
    }

    this[name] = value;
  }

  autosaveRestore(data) {
    this.autosaveAttrs.forEach(name => {
      this._restoreAttribute(name, data);
    });

    this._restoreContacts(data.contacts);

    // Handle insurance card IDs and photo ID IDs
    for (const key of [
      'insuranceCardFrontId',
      'insuranceCardBackId',
      'photoIdFrontId',
      'photoIdBackId',
    ]) {
      if (data[key] !== undefined && data[key] !== null) {
        this.connection?.set(key, data[key]);
      }
    }
  }

  _restoreContacts(contactsData) {
    if (!Array.isArray(contactsData)) return;

    this.contacts.clear();

    contactsData
      .map(data => this._createContactFragment(data))
      .filter(fragment => this._shouldKeepRestoredContact(fragment))
      .forEach(fragment => this.contacts.addObject(fragment));
  }

  _createContactFragment({ isMarkedForDeletion, ...attrs }) {
    const fragment = this.store.createFragment('contact-info/contact', attrs);

    if (isMarkedForDeletion) {
      fragment.set('isMarkedForDeletion', true);
    }

    return fragment;
  }

  _shouldKeepRestoredContact(contact) {
    if (!contact.isNewContact || contact.isMarkedForDeletion) {
      return true;
    }

    return this._meetsPracticeRequirements(contact);
  }

  _meetsPracticeRequirements(contact) {
    if (!contact.emergencyContact) {
      return true;
    }

    const requiredFields = this.demographicFormRequiredFields ?? [];
    const isEmailRequired = requiredFields.includes('emergency_contact_email_address');
    const isPhoneRequired = requiredFields.includes('emergency_contact_phone_number');
    const hasMissingEmail = isEmailRequired && !contact.emailAddress?.trim();
    const hasMissingPhone = isPhoneRequired && !contact.phoneNumber?.trim();

    return !hasMissingEmail && !hasMissingPhone;
  }
}
